/**
 * Supabase client configuration
 * Provides typed client for database operations with Row Level Security
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Lazy initialization to avoid build-time errors when env vars are missing
let supabaseInstance: SupabaseClient | null = null;

/**
 * Client-side Supabase client (uses anon key, respects RLS)
 * Lazy-loaded to avoid build-time errors
 */
export function getSupabase(): SupabaseClient | null {
  // Always check env vars first - return null if not configured (never throw)
  // Check for undefined first, then trim and check for empty string
  const supabaseUrlRaw = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKeyRaw = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  // Return null if either is undefined, null, or empty
  if (!supabaseUrlRaw || !supabaseAnonKeyRaw) {
    return null;
  }

  const supabaseUrl = supabaseUrlRaw.trim();
  const supabaseAnonKey = supabaseAnonKeyRaw.trim();

  // Validate that both are non-empty after trimming
  if (!supabaseUrl || !supabaseAnonKey) {
    // Return null instead of throwing - services will fall back to local storage
    return null;
  }

  // Validate URL format to prevent Supabase library from throwing
  try {
    new URL(supabaseUrl);
  } catch {
    // Invalid URL format - return null instead of throwing
    if (typeof window !== 'undefined') {
      console.warn('Supabase URL is invalid - using local storage fallback');
    }
    return null;
  }

  // Return cached instance if available
  if (supabaseInstance) {
    return supabaseInstance;
  }

  // Create new instance (only if env vars are valid)
  // Double-check that values are not empty strings (Supabase library validates this)
  if (supabaseUrl.length === 0 || supabaseAnonKey.length === 0) {
    if (typeof window !== 'undefined') {
      console.warn('Supabase environment variables are empty - using local storage fallback');
    }
    return null;
  }

  try {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  } catch (error) {
    // If createClient throws (e.g., Supabase library validation), return null
    // Don't log the error to avoid noise - this is expected when Supabase isn't configured
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn(
        'Failed to create Supabase client (this is normal if Supabase is not configured):',
        error
      );
    }
    supabaseInstance = null;
    return null;
  }

  return supabaseInstance;
}

// Note: Do not export supabase directly - it causes errors at module load time
// Always use getSupabase() function instead

/**
 * Server-side Supabase client (uses service role key, bypasses RLS)
 * Only use in API routes with proper authorization checks
 */
export function createServerClient() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!supabaseUrl) {
    throw new Error('NEXT_PUBLIC_SUPABASE_URL is not set');
  }
  if (!serviceRoleKey) {
    throw new Error('SUPABASE_SERVICE_ROLE_KEY is not set');
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  });
}
